import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Search, ArrowUpCircle, ArrowDownCircle, RotateCcw, AlertTriangle, Filter, FileText, Upload, Download, Trash2, Edit, Link2, Package, FolderOpen, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { fmtDate } from '../lib/dateUtils';
import { FilePreview } from '../components/FilePreview';
import { useAuth } from '../contexts/AuthContext';

const fmt = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);

export default function InventoryPage() {
  const { isRole } = useAuth();
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [invDocs, setInvDocs] = useState([]);
  const [docItemFilter, setDocItemFilter] = useState('__all__');
  const [sites, setSites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [customSubcategory, setCustomSubcategory] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [projectSummary, setProjectSummary] = useState([]);
  const [kpi, setKpi] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  // Movement filters
  const [movFilterSite, setMovFilterSite] = useState('');
  const [movFilterProject, setMovFilterProject] = useState('');
  const [movFilterType, setMovFilterType] = useState('');
  // Dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [showMovement, setShowMovement] = useState(false);
  const [showDocCreate, setShowDocCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [showLinkProducts, setShowLinkProducts] = useState(null);
  const [linkSearch, setLinkSearch] = useState('');
  const [movType, setMovType] = useState('carico');
  const [form, setForm] = useState({ name: '', internal_code: '', category: 'materia_prima', subcategory: '', unit_of_measure: 'pz', quantity_available: 0, minimum_stock: 0, warehouse_location: '', default_supplier: '', average_price: 0, last_price: 0, default_vat_pct: 22 });
  const [movForm, setMovForm] = useState({ item_id: '', quantity: '', supplier: '', invoice_reference: '', price_per_unit: '', reason: '', site_id: '', project_id: '', pdl_reference: '', recipient_employee_id: '', date: new Date().toISOString().split('T')[0] });
  const [docForm, setDocForm] = useState({ item_id: '', doc_type: 'scheda_tecnica', title: '', issuing_entity: '', issue_date: '', expiry_date: '', notes: '' });
  const [showEdit, setShowEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [itemsPage, setItemsPage] = useState(1);
  const [movPage, setMovPage] = useState(1);
  const PAGE_SIZE = 25;

  const load = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCategory && filterCategory !== '__all__') params.category = filterCategory;

      const movParams = {};
      if (movFilterSite && movFilterSite !== '__all__') movParams.site_id = movFilterSite;
      if (movFilterProject && movFilterProject !== '__all__') movParams.project_id = movFilterProject;
      if (movFilterType && movFilterType !== '__all__') movParams.movement_type = movFilterType;

      const [itemsRes, movRes, sitesRes, projRes, empRes, catRes, docsRes, subcatRes, suppRes, projSumRes, kpiRes] = await Promise.all([
        api.get('/inventory', { params }),
        api.get('/inventory-movements', { params: movParams }),
        api.get('/sites').catch(() => ({ data: [] })),
        api.get('/projects').catch(() => ({ data: [] })),
        api.get('/employees').catch(() => ({ data: [] })),
        api.get('/categories/inventory').catch(() => ({ data: [] })),
        api.get('/inventory-documents').catch(() => ({ data: [] })),
        api.get('/categories/inventory/subcategories').catch(() => ({ data: [] })),
        api.get('/inventory/suppliers/list').catch(() => ({ data: [] })),
        api.get('/inventory-movements/by-project').catch(() => ({ data: [] })),
        api.get('/inventory-movements/kpi').catch(() => ({ data: {} })),
      ]);
      setItems(itemsRes.data);
      setMovements(movRes.data.slice(0, 200));
      setSites(sitesRes.data);
      setProjects(projRes.data);
      setEmployees(empRes.data);
      setCategories(catRes.data);
      setInvDocs(docsRes.data);
      setSubcategories(subcatRes.data);
      setSuppliers(suppRes.data);
      setProjectSummary(projSumRes.data);
      setKpi(kpiRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filterCategory, movFilterSite, movFilterProject, movFilterType]);

  useEffect(() => { load(); setItemsPage(1); setMovPage(1); }, [load]);

  const siteProjects = movForm.site_id ? projects.filter(p => p.site_id === movForm.site_id) : projects;

  const handleCreate = async () => {
    const finalSubcategory = form.subcategory === '__custom__' ? customSubcategory : (form.subcategory === '__none__' ? '' : form.subcategory);
    await api.post('/inventory', { ...form, subcategory: finalSubcategory, quantity_available: parseFloat(form.quantity_available) || 0, minimum_stock: parseFloat(form.minimum_stock) || 0, average_price: parseFloat(form.average_price) || 0, last_price: parseFloat(form.last_price) || 0, default_vat_pct: parseFloat(form.default_vat_pct) || 22 });
    setShowCreate(false);
    setForm({ name: '', internal_code: '', category: 'materia_prima', subcategory: '', unit_of_measure: 'pz', quantity_available: 0, minimum_stock: 0, warehouse_location: '', default_supplier: '', average_price: 0, last_price: 0, default_vat_pct: 22 });
    setCustomSubcategory('');
    load();
  };

  const openMovement = (type) => {
    setMovType(type);
    setMovForm({ item_id: '', quantity: '', supplier: '', invoice_reference: '', price_per_unit: '', reason: '', site_id: '', project_id: '', pdl_reference: '', recipient_employee_id: '', date: new Date().toISOString().split('T')[0] });
    setShowMovement(true);
  };

  const handleMovement = async () => {
    try {
      await api.post('/inventory-movements', {
        ...movForm,
        movement_type: movType,
        quantity: parseFloat(movForm.quantity) || 0,
        price_per_unit: movForm.price_per_unit ? parseFloat(movForm.price_per_unit) : null,
        project_id: movForm.project_id || null,
      });
      setShowMovement(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDocCreate = async () => {
    try {
      await api.post('/inventory-documents', docForm);
      setShowDocCreate(false);
      setDocForm({ item_id: '', doc_type: 'scheda_tecnica', title: '', issuing_entity: '', issue_date: '', expiry_date: '', notes: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleUploadDoc = async (docId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/inventory-documents/${docId}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowUpload(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore upload'); }
  };

  const handleDownloadItemDocsZip = async () => {
    try {
      const res = await api.get('/inventory-documents/zip', { params: { item_id: docItemFilter }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      const cd = res.headers['content-disposition'] || '';
      a.download = cd.split('filename=')[1] || 'documenti.zip';
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || 'Errore scaricamento ZIP'); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try { await api.delete(`/inventory-documents/${docId}`); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openEditItem = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.name || '', internal_code: item.internal_code || '', category: item.category || 'materia_prima',
      subcategory: item.subcategory || '', unit_of_measure: item.unit_of_measure || 'pz',
      quantity_available: item.quantity_available || 0, minimum_stock: item.minimum_stock || 0,
      warehouse_location: item.warehouse_location || '', default_supplier: item.default_supplier || '',
      average_price: item.average_price || 0, last_price: item.last_price || 0, default_vat_pct: item.default_vat_pct ?? 22, notes: item.notes || '',
    });
    setShowEdit(true);
  };

  const handleEditItem = async () => {
    try {
      await api.put(`/inventory/${editItem.id}`, { ...editForm, quantity_available: parseFloat(editForm.quantity_available) || 0, minimum_stock: parseFloat(editForm.minimum_stock) || 0, average_price: parseFloat(editForm.average_price) || 0, last_price: parseFloat(editForm.last_price) || 0, default_vat_pct: parseFloat(editForm.default_vat_pct) || 22 });
      setShowEdit(false);
      setEditItem(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Eliminare "${item.name}"?`)) return;
    try { await api.delete(`/inventory/${item.id}`); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleLinkProducts = async (itemId, linkedIds) => {
    try {
      await api.put(`/inventory/${itemId}/link-products`, { linked_product_ids: linkedIds });
      setShowLinkProducts(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const getLinkedNames = (item) => {
    const linked = item.linked_products || [];
    return linked.length === 0 ? null : linked.map(lid => items.find(i => i.id === lid)?.name).filter(Boolean);
  };

  const lowStock = items.filter(i => i.is_low_stock);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Caricamento magazzino...</div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Magazzino</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 sm:w-44">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cerca articolo..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="inv-search" />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-36 h-9" data-testid="filter-category"><Filter size={14} className="mr-1" /><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte</SelectItem>
              <SelectItem value="materia_prima">Materia Prima</SelectItem>
              <SelectItem value="materiale_consumo">Materiale Consumo</SelectItem>
              {categories.filter(c => !['materia_prima', 'materiale_consumo'].includes(c)).map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
            </SelectContent>
          </Select>
          {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && (<>
            <Button variant="outline" size="sm" onClick={() => openMovement('carico')} data-testid="load-btn" className="text-green-700 border-green-300 hover:bg-green-50"><ArrowUpCircle size={14} className="mr-1" /> Carico</Button>
            <Button variant="outline" size="sm" onClick={() => openMovement('scarico')} data-testid="unload-btn" className="text-red-700 border-red-300 hover:bg-red-50"><ArrowDownCircle size={14} className="mr-1" /> Scarico</Button>
            <Button variant="outline" size="sm" onClick={() => openMovement('reso')} data-testid="return-btn" className="text-blue-700 border-blue-300 hover:bg-blue-50"><RotateCcw size={14} className="mr-1" /> Reso</Button>
            <Button variant="outline" size="sm" onClick={() => setShowDocCreate(true)} data-testid="add-doc-btn"><FileText size={14} className="mr-1" /> Doc</Button>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="add-item-btn"><Plus size={14} className="mr-1" /> Articolo</Button>
          </>)}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card className="border-slate-200"><CardContent className="p-3 text-center"><Package size={16} className="mx-auto text-slate-500 mb-1" /><p className="text-xs text-slate-500">Articoli</p><p className="text-lg font-bold text-slate-700" data-testid="kpi-items">{kpi.total_items || 0}</p></CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50/50"><CardContent className="p-3 text-center"><TrendingUp size={16} className="mx-auto text-emerald-600 mb-1" /><p className="text-xs text-emerald-600">Valore Magazzino</p><p className="text-base font-bold text-emerald-700" data-testid="kpi-value">{fmt(kpi.total_value)}</p></CardContent></Card>
        <Card className="border-red-200 bg-red-50/50"><CardContent className="p-3 text-center"><AlertTriangle size={16} className="mx-auto text-red-600 mb-1" /><p className="text-xs text-red-600">Scorte Basse</p><p className="text-lg font-bold text-red-700" data-testid="kpi-lowstock">{kpi.low_stock_count || 0}</p></CardContent></Card>
        <Card className="border-green-200 bg-green-50/30"><CardContent className="p-3 text-center"><ArrowUpCircle size={16} className="mx-auto text-green-600 mb-1" /><p className="text-xs text-green-600">Carichi Mese</p><p className="text-lg font-bold text-green-700" data-testid="kpi-carichi">{kpi.carichi_mese || 0}</p></CardContent></Card>
        <Card className="border-orange-200 bg-orange-50/30"><CardContent className="p-3 text-center"><ArrowDownCircle size={16} className="mx-auto text-orange-600 mb-1" /><p className="text-xs text-orange-600">Scarichi Mese</p><p className="text-lg font-bold text-orange-700" data-testid="kpi-scarichi">{kpi.scarichi_mese || 0}</p></CardContent></Card>
        <Card className="border-blue-200 bg-blue-50/30"><CardContent className="p-3 text-center"><RotateCcw size={16} className="mx-auto text-blue-600 mb-1" /><p className="text-xs text-blue-600">Resi Mese</p><p className="text-lg font-bold text-blue-700" data-testid="kpi-resi">{kpi.resi_mese || 0}</p></CardContent></Card>
        <Card className="border-purple-200 bg-purple-50/30"><CardContent className="p-3 text-center"><FolderOpen size={16} className="mx-auto text-purple-600 mb-1" /><p className="text-xs text-purple-600">Costo Scarichi</p><p className="text-base font-bold text-purple-700" data-testid="kpi-cost">{fmt(kpi.costo_scarichi_mese)}</p></CardContent></Card>
      </div>

      <Tabs defaultValue="articoli">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="articoli">Articoli ({items.length})</TabsTrigger>
          <TabsTrigger value="movimenti">Movimenti ({movements.length})</TabsTrigger>
          <TabsTrigger value="progetto" data-testid="tab-progetto">Per Progetto ({projectSummary.length})</TabsTrigger>
          <TabsTrigger value="documenti">Documenti ({invDocs.length})</TabsTrigger>
          <TabsTrigger value="scorte">Scorte Basse ({lowStock.length})</TabsTrigger>
        </TabsList>

        {/* ─── ARTICOLI ─── */}
        <TabsContent value="articoli">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Articolo</TableHead>
                    <TableHead className="hidden sm:table-cell">Codice</TableHead>
                    <TableHead>Cat.</TableHead>
                    <TableHead className="text-right">Disp.</TableHead>
                    <TableHead className="text-right hidden sm:table-cell">Min.</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="hidden md:table-cell">Collegati</TableHead>
                    {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && <TableHead className="w-28">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Nessun articolo</TableCell></TableRow>
                  ) : items.slice((itemsPage - 1) * PAGE_SIZE, itemsPage * PAGE_SIZE).map(item => (
                    <TableRow key={item.id} className="hover:bg-slate-50" data-testid={`inv-row-${item.id}`}>
                      <TableCell><p className="text-sm font-medium">{item.name}</p><p className="text-xs text-slate-400">{item.default_supplier || ''}</p></TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-slate-600">{item.internal_code || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">{item.category === 'materia_prima' ? 'MP' : item.category === 'materiale_consumo' ? 'MC' : item.category}</Badge></TableCell>
                      <TableCell className="text-right text-sm font-medium">{item.quantity_available} {item.unit_of_measure}</TableCell>
                      <TableCell className="text-right hidden sm:table-cell text-sm text-slate-500">{item.minimum_stock} {item.unit_of_measure}</TableCell>
                      <TableCell>
                        {item.is_low_stock ? <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><AlertTriangle size={12} className="mr-1" />Bassa</Badge> : <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">OK</Badge>}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {(() => { const linked = getLinkedNames(item); return !linked ? <span className="text-xs text-slate-400">-</span> : <div className="flex flex-wrap gap-1">{linked.map((n, i) => <Badge key={i} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">{n}</Badge>)}</div>; })()}
                      </TableCell>
                      {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setShowLinkProducts(item)} className="p-1 h-auto text-purple-600" title="Collega" data-testid={`link-inv-${item.id}`}><Link2 size={14} /></Button>
                            <Button variant="ghost" size="sm" onClick={() => openEditItem(item)} className="p-1 h-auto text-blue-600" data-testid={`edit-inv-${item.id}`}><Edit size={14} /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item)} className="p-1 h-auto text-red-600" data-testid={`del-inv-${item.id}`}><Trash2 size={14} /></Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          {items.length > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-3 px-1">
              <p className="text-xs text-slate-500">
                {(itemsPage - 1) * PAGE_SIZE + 1}-{Math.min(itemsPage * PAGE_SIZE, items.length)} di {items.length} articoli
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={itemsPage <= 1} onClick={() => setItemsPage(p => p - 1)} data-testid="items-prev-page">
                  <ChevronLeft size={14} />
                </Button>
                <span className="text-xs text-slate-600 px-2">Pag. {itemsPage}/{Math.ceil(items.length / PAGE_SIZE)}</span>
                <Button variant="outline" size="sm" disabled={itemsPage >= Math.ceil(items.length / PAGE_SIZE)} onClick={() => setItemsPage(p => p + 1)} data-testid="items-next-page">
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ─── MOVIMENTI ─── */}
        <TabsContent value="movimenti">
          <div className="flex flex-wrap gap-2 mt-4 mb-3">
            <Select value={movFilterType} onValueChange={setMovFilterType}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="mov-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Tutti</SelectItem><SelectItem value="carico">Carico</SelectItem><SelectItem value="scarico">Scarico</SelectItem><SelectItem value="reso">Reso</SelectItem></SelectContent>
            </Select>
            <Select value={movFilterSite} onValueChange={setMovFilterSite}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="mov-filter-site"><SelectValue placeholder="Cantiere" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Tutti</SelectItem>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={movFilterProject} onValueChange={setMovFilterProject}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="mov-filter-project"><SelectValue placeholder="Progetto" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Tutti</SelectItem>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Articolo</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Cantiere</TableHead>
                    <TableHead>Progetto</TableHead>
                    <TableHead className="hidden md:table-cell">Dettaglio</TableHead>
                    <TableHead className="hidden lg:table-cell">Registrato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Nessun movimento</TableCell></TableRow>
                  ) : movements.slice((movPage - 1) * PAGE_SIZE, movPage * PAGE_SIZE).map(m => (
                    <TableRow key={m.id} className="hover:bg-slate-50" data-testid={`mov-row-${m.id}`}>
                      <TableCell className="text-sm">{m.date}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={m.movement_type === 'carico' ? 'bg-green-50 text-green-700 border-green-200' : m.movement_type === 'reso' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-red-50 text-red-700 border-red-200'}>
                          {m.movement_type === 'carico' ? '+' : m.movement_type === 'reso' ? '↩' : '-'} {m.movement_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-medium">{m.item_name}</TableCell>
                      <TableCell className="text-right text-sm font-medium">{m.quantity}</TableCell>
                      <TableCell className="text-xs text-slate-600">{m.site_name || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-600">{m.project_name || '-'}</TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-slate-500">
                        {m.movement_type === 'carico' ? (m.supplier || '') : (m.recipient_name || m.reason || '')}
                        {m.total_price ? ` (${fmt(m.total_price)})` : ''}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-slate-500">{m.registered_by_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          {movements.length > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-3 px-1">
              <p className="text-xs text-slate-500">
                {(movPage - 1) * PAGE_SIZE + 1}-{Math.min(movPage * PAGE_SIZE, movements.length)} di {movements.length} movimenti
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={movPage <= 1} onClick={() => setMovPage(p => p - 1)} data-testid="mov-prev-page">
                  <ChevronLeft size={14} />
                </Button>
                <span className="text-xs text-slate-600 px-2">Pag. {movPage}/{Math.ceil(movements.length / PAGE_SIZE)}</span>
                <Button variant="outline" size="sm" disabled={movPage >= Math.ceil(movements.length / PAGE_SIZE)} onClick={() => setMovPage(p => p + 1)} data-testid="mov-next-page">
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ─── PER PROGETTO ─── */}
        <TabsContent value="progetto">
          <div className="space-y-4 mt-4">
            {projectSummary.length === 0 ? (
              <Card className="border-slate-200"><CardContent className="py-8 text-center text-slate-400">Nessuno scarico collegato a un progetto</CardContent></Card>
            ) : projectSummary.map(ps => (
              <Card key={ps.project_id} className="border-slate-200 shadow-sm" data-testid={`proj-summary-${ps.project_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">{ps.project_name}</CardTitle>
                      <p className="text-xs text-slate-500">{ps.site_name} - {ps.total_movements} movimenti</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Costo Netto Materiali</p>
                      <p className={`text-lg font-bold ${ps.total_cost >= 0 ? 'text-red-700' : 'text-green-700'}`} data-testid={`proj-cost-${ps.project_id}`}>{fmt(ps.total_cost)}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Articolo</TableHead>
                        <TableHead className="text-xs text-right">Scaricato</TableHead>
                        <TableHead className="text-xs text-right">Reso</TableHead>
                        <TableHead className="text-xs text-right">Netto</TableHead>
                        <TableHead className="text-xs text-right">Costo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ps.items.map((it, i) => (
                        <TableRow key={i} className="hover:bg-slate-50">
                          <TableCell className="text-sm">{it.item_name}</TableCell>
                          <TableCell className="text-right text-sm text-red-600">{it.qty_scarico}</TableCell>
                          <TableCell className="text-right text-sm text-blue-600">{it.qty_reso}</TableCell>
                          <TableCell className="text-right text-sm font-medium">{it.qty_scarico - it.qty_reso}</TableCell>
                          <TableCell className="text-right text-sm font-medium">{fmt(it.cost)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ─── DOCUMENTI ─── */}
        <TabsContent value="documenti">
          <div className="flex flex-wrap items-center gap-2 mt-4 mb-3">
            <Select value={docItemFilter} onValueChange={setDocItemFilter}>
              <SelectTrigger className="w-64" data-testid="doc-item-filter"><SelectValue placeholder="Filtra per articolo..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti gli articoli</SelectItem>
                {items.map(it => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {docItemFilter !== '__all__' && (
              <Button variant="outline" onClick={handleDownloadItemDocsZip} data-testid="download-item-docs-zip-btn">
                <Download size={16} className="mr-1" /> Scarica ZIP
              </Button>
            )}
          </div>
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Articolo</TableHead><TableHead>Documento</TableHead><TableHead>Tipo</TableHead><TableHead>Scadenza</TableHead><TableHead>Stato</TableHead><TableHead>File</TableHead>{isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && <TableHead className="w-20">Azioni</TableHead>}</TableRow></TableHeader>
                <TableBody>
                  {invDocs.filter(d => docItemFilter === '__all__' || d.item_id === docItemFilter).length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nessun documento</TableCell></TableRow>
                  ) : invDocs.filter(d => docItemFilter === '__all__' || d.item_id === docItemFilter).map(doc => (
                    <TableRow key={doc.id} className="hover:bg-slate-50">
                      <TableCell className="text-sm font-medium">{doc.item_name || 'N/A'}</TableCell>
                      <TableCell className="text-sm">{doc.title}</TableCell>
                      <TableCell><Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">{doc.doc_type?.replace('_', ' ')}</Badge></TableCell>
                      <TableCell className="text-sm">{doc.expiry_date ? fmtDate(doc.expiry_date) : 'N/A'}</TableCell>
                      <TableCell>
                        {doc.status === 'scaduto' ? <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Scaduto</Badge> : doc.status === 'in_scadenza' ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">In Scad.</Badge> : <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Valido</Badge>}
                      </TableCell>
                      <TableCell>
                        {doc.file_url ? <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm flex items-center gap-1"><Download size={14} /> Scarica</a>
                        : isRole('admin', 'aspp', 'qhse', 'capo_cantiere') ? <Button variant="ghost" size="sm" onClick={() => setShowUpload(doc.id)} className="text-xs p-1 h-auto"><Upload size={14} className="mr-1" /> Carica</Button>
                        : <span className="text-slate-400 text-xs">-</span>}
                      </TableCell>
                      {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && <TableCell><div className="flex gap-1">{!doc.file_url && <Button variant="ghost" size="sm" onClick={() => setShowUpload(doc.id)} className="p-1 h-auto text-blue-600"><Upload size={14} /></Button>}<Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)} className="p-1 h-auto text-red-600"><Trash2 size={14} /></Button></div></TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── SCORTE BASSE ─── */}
        <TabsContent value="scorte">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Articolo</TableHead><TableHead className="text-right">Disponibile</TableHead><TableHead className="text-right">Minimo</TableHead><TableHead>Fornitore</TableHead></TableRow></TableHeader>
                <TableBody>
                  {lowStock.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-green-600">Tutte le scorte sono sopra il livello minimo</TableCell></TableRow>
                  ) : lowStock.map(item => (
                    <TableRow key={item.id} className="bg-red-50/50"><TableCell className="text-sm font-medium">{item.name}</TableCell><TableCell className="text-right text-sm font-bold text-red-700">{item.quantity_available} {item.unit_of_measure}</TableCell><TableCell className="text-right text-sm text-slate-500">{item.minimum_stock} {item.unit_of_measure}</TableCell><TableCell className="text-sm text-slate-600">{item.default_supplier || '-'}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ─── CREATE ITEM DIALOG ─── */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuovo Articolo</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Nome *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="inv-name" /></div>
            <div className="space-y-1.5"><Label>Codice Interno</Label><Input value={form.internal_code} onChange={e => setForm({ ...form, internal_code: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Categoria</Label><Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="materia_prima">Materia Prima</SelectItem><SelectItem value="materiale_consumo">Materiale Consumo</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Sottocategoria</Label><Select value={form.subcategory || "__none__"} onValueChange={v => setForm({ ...form, subcategory: v === "__none__" ? "" : v })}><SelectTrigger data-testid="inv-subcategory"><SelectValue placeholder="Seleziona..." /></SelectTrigger><SelectContent><SelectItem value="__none__">Nessuna</SelectItem>{subcategories.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}<SelectItem value="__custom__">+ Nuova...</SelectItem></SelectContent></Select></div>
            {form.subcategory === '__custom__' && <div className="space-y-1.5"><Label>Nome Sottocategoria</Label><Input value={customSubcategory} onChange={e => setCustomSubcategory(e.target.value)} data-testid="inv-custom-subcategory" /></div>}
            <div className="space-y-1.5"><Label>U.M.</Label><Input value={form.unit_of_measure} onChange={e => setForm({ ...form, unit_of_measure: e.target.value })} placeholder="pz, kg, lt, m" /></div>
            <div className="space-y-1.5"><Label>Qty Iniziale</Label><Input type="number" value={form.quantity_available} onChange={e => setForm({ ...form, quantity_available: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Scorta Minima</Label><Input type="number" value={form.minimum_stock} onChange={e => setForm({ ...form, minimum_stock: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Posizione</Label><Input value={form.warehouse_location} onChange={e => setForm({ ...form, warehouse_location: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Fornitore</Label><Input value={form.default_supplier} onChange={e => setForm({ ...form, default_supplier: e.target.value })} list="supplier-list" placeholder="Seleziona o digita..." data-testid="inv-supplier" /><datalist id="supplier-list">{suppliers.map((s, i) => <option key={i} value={s} />)}</datalist></div>
            <div className="space-y-1.5"><Label>Prezzo Unitario (€)</Label><Input type="number" step="0.01" value={form.last_price} onChange={e => setForm({ ...form, last_price: e.target.value })} placeholder="0.00" data-testid="inv-price" /></div>
            <div className="space-y-1.5"><Label>IVA %</Label><Input type="number" value={form.default_vat_pct} onChange={e => setForm({ ...form, default_vat_pct: e.target.value })} placeholder="22" data-testid="inv-vat" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button><Button onClick={handleCreate} disabled={!form.name} data-testid="inv-save-btn">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── MOVEMENT DIALOG ─── */}
      <Dialog open={showMovement} onOpenChange={setShowMovement}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{movType === 'carico' ? 'Carico Materiale' : movType === 'reso' ? 'Reso Materiale' : 'Scarico Materiale'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Articolo *</Label>
              <Select value={movForm.item_id} onValueChange={v => setMovForm({ ...movForm, item_id: v })}>
                <SelectTrigger data-testid="mov-item-select"><SelectValue placeholder="Seleziona articolo..." /></SelectTrigger>
                <SelectContent>{items.map(i => <SelectItem key={i.id} value={i.id}>{i.name} (disp: {i.quantity_available} {i.unit_of_measure})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Quantita *</Label><Input type="number" value={movForm.quantity} onChange={e => setMovForm({ ...movForm, quantity: e.target.value })} data-testid="mov-qty" /></div>
            <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={movForm.date} onChange={e => setMovForm({ ...movForm, date: e.target.value })} data-testid="mov-date" /></div>

            {movType === 'carico' ? (<>
              <div className="space-y-1.5"><Label>Prezzo Unitario</Label><Input type="number" step="0.01" value={movForm.price_per_unit} onChange={e => setMovForm({ ...movForm, price_per_unit: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Fornitore</Label><Input value={movForm.supplier} onChange={e => setMovForm({ ...movForm, supplier: e.target.value })} list="supplier-list-mov" /><datalist id="supplier-list-mov">{suppliers.map((s, i) => <option key={i} value={s} />)}</datalist></div>
              <div className="space-y-1.5"><Label>Rif. Fattura</Label><Input value={movForm.invoice_reference} onChange={e => setMovForm({ ...movForm, invoice_reference: e.target.value })} /></div>
            </>) : (<>
              {/* Scarico e Reso: cantiere + progetto */}
              <div className="space-y-1.5">
                <Label>Cantiere</Label>
                <Select value={movForm.site_id} onValueChange={v => setMovForm({ ...movForm, site_id: v, project_id: '' })}>
                  <SelectTrigger data-testid="mov-site-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Progetto</Label>
                <Select value={movForm.project_id || '__none__'} onValueChange={v => setMovForm({ ...movForm, project_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger data-testid="mov-project-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessuno</SelectItem>
                    {siteProjects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Causale</Label><Input value={movForm.reason} onChange={e => setMovForm({ ...movForm, reason: e.target.value })} data-testid="mov-reason" /></div>
              <div className="space-y-1.5"><Label>PDL</Label><Input value={movForm.pdl_reference} onChange={e => setMovForm({ ...movForm, pdl_reference: e.target.value })} /></div>
              {movType === 'scarico' && (
                <div className="col-span-2 space-y-1.5">
                  <Label>Dipendente Destinatario</Label>
                  <Select value={movForm.recipient_employee_id} onValueChange={v => setMovForm({ ...movForm, recipient_employee_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </>)}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMovement(false)}>Annulla</Button>
            <Button onClick={handleMovement} disabled={!movForm.item_id || !movForm.quantity} data-testid="mov-save-btn">
              {movType === 'carico' ? 'Registra Carico' : movType === 'reso' ? 'Registra Reso' : 'Registra Scarico'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── DOC CREATE DIALOG ─── */}
      <Dialog open={showDocCreate} onOpenChange={setShowDocCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuovo Documento Articolo</DialogTitle><DialogDescription>Aggiungi scheda tecnica, certificazione o altro documento</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Articolo *</Label><Select value={docForm.item_id} onValueChange={v => setDocForm({ ...docForm, item_id: v })}><SelectTrigger data-testid="inv-doc-item"><SelectValue placeholder="Seleziona..." /></SelectTrigger><SelectContent>{items.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="col-span-2 space-y-1.5"><Label>Titolo *</Label><Input value={docForm.title} onChange={e => setDocForm({ ...docForm, title: e.target.value })} data-testid="inv-doc-title" /></div>
            <div className="space-y-1.5"><Label>Tipo</Label><Select value={docForm.doc_type} onValueChange={v => setDocForm({ ...docForm, doc_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="scheda_tecnica">Scheda Tecnica</SelectItem><SelectItem value="msds">Scheda di Sicurezza (MSDS)</SelectItem><SelectItem value="bolla">Bolla</SelectItem><SelectItem value="certificazione">Certificazione</SelectItem><SelectItem value="garanzia">Garanzia</SelectItem><SelectItem value="altro">Altro</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Ente Emittente</Label><Input value={docForm.issuing_entity} onChange={e => setDocForm({ ...docForm, issuing_entity: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Emissione</Label><Input type="date" value={docForm.issue_date} onChange={e => setDocForm({ ...docForm, issue_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Scadenza</Label><Input type="date" value={docForm.expiry_date} onChange={e => setDocForm({ ...docForm, expiry_date: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Input value={docForm.notes} onChange={e => setDocForm({ ...docForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowDocCreate(false)}>Annulla</Button><Button onClick={handleDocCreate} disabled={!docForm.item_id || !docForm.title} data-testid="inv-doc-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Doc */}
      <Dialog open={!!showUpload} onOpenChange={(o) => { if (!o) { setShowUpload(null); setPendingFile(null); } }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Carica File</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xls,.xlsx" onChange={e => setPendingFile(e.target.files?.[0] || null)} data-testid="upload-file-input" />
            <p className="text-xs text-slate-500">Formati: PDF, DOC, immagini, Excel</p>
            <FilePreview file={pendingFile} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowUpload(null); setPendingFile(null); }}>Annulla</Button>
            <Button onClick={() => { handleUploadDoc(showUpload, pendingFile); setPendingFile(null); }} disabled={!pendingFile} data-testid="upload-confirm-btn">Carica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica Articolo</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Nome *</Label><Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-inv-name" /></div>
            <div className="space-y-1.5"><Label>Codice Interno</Label><Input value={editForm.internal_code} onChange={e => setEditForm({ ...editForm, internal_code: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Categoria</Label><Select value={editForm.category} onValueChange={v => setEditForm({ ...editForm, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="materia_prima">Materia Prima</SelectItem><SelectItem value="materiale_consumo">Materiale Consumo</SelectItem></SelectContent></Select></div>
            <div className="space-y-1.5"><Label>U.M.</Label><Input value={editForm.unit_of_measure} onChange={e => setEditForm({ ...editForm, unit_of_measure: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Qty</Label><Input type="number" value={editForm.quantity_available} onChange={e => setEditForm({ ...editForm, quantity_available: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Scorta Min.</Label><Input type="number" value={editForm.minimum_stock} onChange={e => setEditForm({ ...editForm, minimum_stock: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Posizione</Label><Input value={editForm.warehouse_location} onChange={e => setEditForm({ ...editForm, warehouse_location: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Fornitore</Label><Input value={editForm.default_supplier} onChange={e => setEditForm({ ...editForm, default_supplier: e.target.value })} list="supplier-list-edit" /><datalist id="supplier-list-edit">{suppliers.map((s, i) => <option key={i} value={s} />)}</datalist></div>
            <div className="space-y-1.5"><Label>Prezzo Unitario (€)</Label><Input type="number" step="0.01" value={editForm.last_price} onChange={e => setEditForm({ ...editForm, last_price: e.target.value })} placeholder="0.00" /></div>
            <div className="space-y-1.5"><Label>IVA %</Label><Input type="number" value={editForm.default_vat_pct} onChange={e => setEditForm({ ...editForm, default_vat_pct: e.target.value })} placeholder="22" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowEdit(false)}>Annulla</Button><Button onClick={handleEditItem} disabled={!editForm.name} data-testid="edit-inv-save-btn">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Products */}
      <Dialog open={!!showLinkProducts} onOpenChange={() => { setShowLinkProducts(null); setLinkSearch(''); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Collega Prodotti a "{showLinkProducts?.name}"</DialogTitle>
            <DialogDescription>Seleziona articoli correlati</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Cerca articolo..." value={linkSearch} onChange={e => setLinkSearch(e.target.value)} data-testid="link-search" />
            <div className="max-h-60 overflow-y-auto border rounded-md divide-y">
              {items.filter(i => i.id !== showLinkProducts?.id).filter(i => !linkSearch || i.name.toLowerCase().includes(linkSearch.toLowerCase())).map(i => {
                const isLinked = (showLinkProducts?.linked_products || []).includes(i.id);
                return (
                  <div key={i.id} className={`flex items-center gap-3 p-2 cursor-pointer hover:bg-slate-50 ${isLinked ? 'bg-blue-50' : ''}`}
                    onClick={() => { const current = showLinkProducts?.linked_products || []; setShowLinkProducts({ ...showLinkProducts, linked_products: isLinked ? current.filter(lid => lid !== i.id) : [...current, i.id] }); }} data-testid={`link-item-${i.id}`}>
                    <input type="checkbox" checked={isLinked} readOnly className="rounded" />
                    <div className="flex-1"><p className="text-sm font-medium">{i.name}</p><p className="text-xs text-slate-500">{i.internal_code || ''}</p></div>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setShowLinkProducts(null); setLinkSearch(''); }}>Annulla</Button><Button onClick={() => handleLinkProducts(showLinkProducts.id, showLinkProducts.linked_products || [])} data-testid="link-save-btn">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
